import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from './firebase/authReadyCallable';
import { db, functions } from '../config/firebase';
import type {
  Quote,
  QuoteLine,
  QuoteUnit,
  User,
} from '../types/firestore';

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

export const collections = {
  quotes: () => collection(db, 'quotes'),
  quoteLines: (quoteId: string) => collection(db, `quotes/${quoteId}/lines`),
  quoteUnits: (quoteId: string) => collection(db, `quotes/${quoteId}/units`),
  quoteGroupings: (quoteId: string) => collection(db, `quotes/${quoteId}/groupings`),
  users: () => collection(db, 'users'),
  customers: () => collection(db, 'customers'),
  rateSheets: () => collection(db, 'rateSheets'),
  parts: () => collection(db, 'parts'),
  kitTemplates: () => collection(db, 'kitTemplates'),
  procedureTemplates: () => collection(db, 'procedureTemplates'),
  termsAndConditions: () => collection(db, 'termsAndConditions'),
  organizations: () => collection(db, 'organizations'),
  quoteApprovals: () => collection(db, 'quoteApprovals'),
  // P5 Customer Support
  callLogs: () => collection(db, 'callLogs'),
};

// ============================================================================
// DOCUMENT REFERENCES
// ============================================================================

export const docRef = {
  quote: (quoteId: string) => doc(db, 'quotes', quoteId),
  quoteLine: (quoteId: string, lineId: string) => doc(db, `quotes/${quoteId}/lines`, lineId),
  quoteUnit: (quoteId: string, unitId: string) => doc(db, `quotes/${quoteId}/units`, unitId),
  user: (userId: string) => doc(db, 'users', userId),
  customer: (customerId: string) => doc(db, 'customers', customerId),
  rateSheet: (rateSheetId: string) => doc(db, 'rateSheets', rateSheetId),
  organization: (orgId: string) => doc(db, 'organizations', orgId),
  // P5 Customer Support
  callLog: (callLogId: string) => doc(db, 'callLogs', callLogId),
};

// ============================================================================
// QUOTE OPERATIONS
// ============================================================================

/**
 * Get a single quote by ID
 */
export async function getQuote(quoteId: string): Promise<Quote | null> {
  const quoteDoc = await getDoc(docRef.quote(quoteId));
  if (!quoteDoc.exists()) {
    return null;
  }
  return quoteDoc.data() as Quote;
}

/**
 * Get all lines for a quote
 */
export async function getQuoteLines(quoteId: string): Promise<QuoteLine[]> {
  const linesQuery = query(
    collections.quoteLines(quoteId),
    orderBy('displayOrder', 'asc')
  );
  const snapshot = await getDocs(linesQuery);
  return snapshot.docs.map(doc => doc.data() as QuoteLine);
}

/**
 * Get all units for a quote
 */
export async function getQuoteUnits(quoteId: string): Promise<QuoteUnit[]> {
  const snapshot = await getDocs(collections.quoteUnits(quoteId));
  return snapshot.docs.map(doc => doc.data() as QuoteUnit);
}

/**
 * Remove undefined values from an object to prevent Firestore errors.
 * Firestore's updateDoc doesn't accept undefined values.
 */
function removeUndefinedValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>;
}

/**
 * Update quote fields
 * Note: undefined values are automatically filtered out to prevent Firestore errors
 */
export async function updateQuote(
  quoteId: string,
  updates: Partial<Quote>
): Promise<void> {
  const cleanedUpdates = removeUndefinedValues(updates);
  const fn = httpsCallable(functions, 'shared_updateQuote');
  await fn({ quoteId, updates: cleanedUpdates });
}

/**
 * Add a line item to a quote
 */
export async function addQuoteLine(
  quoteId: string,
  line: Omit<QuoteLine, 'lineId' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const fn = httpsCallable<unknown, { lineId: string }>(functions, 'shared_addQuoteLine');
  const result = await fn({ quoteId, line });
  return result.data.lineId;
}

/**
 * Update a quote line item
 */
export async function updateQuoteLine(
  quoteId: string,
  lineId: string,
  updates: Partial<QuoteLine>
): Promise<void> {
  const fn = httpsCallable(functions, 'shared_updateQuoteLine');
  await fn({ quoteId, lineId, updates });
}

/**
 * Delete a quote line item
 */
export async function deleteQuoteLine(
  quoteId: string,
  lineId: string
): Promise<void> {
  const fn = httpsCallable(functions, 'shared_deleteQuoteLine');
  await fn({ quoteId, lineId });
}

// ============================================================================
// USER OPERATIONS
// ============================================================================

/**
 * Get user profile
 */
export async function getUserProfile(userId: string): Promise<User | null> {
  const userDoc = await getDoc(docRef.user(userId));
  if (!userDoc.exists()) {
    return null;
  }
  return userDoc.data() as User;
}

// ============================================================================
// TIMESTAMP HELPERS
// ============================================================================

/**
 * Safely convert a timestamp-like value to a Date.
 * Handles native Firestore Timestamps, serialized objects from httpsCallable
 * (which lose .toDate()), ISO strings, and epoch numbers.
 */
export function safeTimestampToDate(
  value: unknown
): Date | null {
  if (!value) return null;

  // Native Firestore Timestamp with .toDate()
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: unknown }).toDate === 'function') {
    return (value as Timestamp).toDate();
  }

  // Serialized timestamp from httpsCallable: { _seconds, _nanoseconds } or { seconds, nanoseconds }
  const obj = value as Record<string, unknown>;
  const seconds = (obj._seconds ?? obj.seconds) as number | undefined;
  if (typeof seconds === 'number') {
    return new Date(seconds * 1000);
  }

  // ISO string
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // Epoch milliseconds
  if (typeof value === 'number') {
    return new Date(value);
  }

  return null;
}

